// src/components/ErrorBoundary.jsx
// No error boundary previously existed anywhere in this app. React unmounts
// the entire tree on any uncaught render-time exception when there's no
// boundary to catch it — that's the actual mechanism behind a route going
// completely blank instead of showing a usable fallback. This is a minimal,
// generic boundary meant to be dropped around any route.
//
// FIX (July 2026): the default fallback text said "contact your accounting
// firm" unconditionally. That wording is correct for the public, client-
// facing routes (/signoff/:token, /intake/:token) where the person reading
// it genuinely is an external client of the firm. It was wrong on every
// ProtectedRoute-wrapped internal page (Bank Statement, Trial Balance,
// Notes, etc.) — the person seeing it there IS the accounting firm, so
// telling them to "contact your accounting firm" made no sense. `audience`
// controls which wording is shown:
//   'firm'   (default) — internal, logged-in firm staff. Used by
//             ProtectedRoute and any other internal route.
//   'client' — external, unauthenticated people (director signing off,
//             client submitting an intake). Pass explicitly at the call
//             site for /signoff/:token and /intake/:token.
import { Component } from 'react'

import Icon from './Icon'
import { T } from '../theme/theme'
const MESSAGES = {
  firm: {
    heading: 'Something went wrong',
    body: 'This page hit an unexpected error. Try reloading — if it keeps happening, contact KaiLux support.',
  },
  client: {
    heading: 'Something went wrong',
    body: 'This page hit an unexpected error. Try reloading — if it keeps happening, contact your accounting firm.',
  },
}

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, info) {
    // Non-fatal — just logged. Wire to Sentry here if/when this boundary
    // needs to report to the existing error-monitoring setup.
    console.error('ErrorBoundary caught:', error, info)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      const { heading, body } = MESSAGES[this.props.audience] || MESSAGES.firm
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
          justifyContent: 'center', padding: '24px', fontFamily: "'Outfit',sans-serif",
          background: T.bg }}>
          <div style={{ maxWidth: '420px', width: '100%', textAlign: 'center',
            background: T.s2, borderRadius: '16px', border: `1px solid ${T.bd}`,
            boxShadow: T.sh, padding: '28px 24px' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}><Icon name="alert" strokeWidth={1.5} style={{ color: T.am }} /></div>
            <div style={{ fontSize: '17px', fontWeight: '800', color: T.tx,
              marginBottom: '8px' }}>
              {heading}
            </div>
            <div style={{ fontSize: '13px', color: T.mt, lineHeight: '1.7',
              marginBottom: '18px' }}>
              {body}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '11px 20px', borderRadius: '10px', border: 'none',
                background: T.prg, color: T.onpri, fontFamily: "'Outfit',sans-serif",
                fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
              Reload page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
